import { getAPIErrorDetailAndMessage } from "@/api/helpers";
import { ErrorDisplay } from "@/components/ErrorDisplay";
import { LoadingCover } from "@/components/LoadingCover";
import { OrganizationServiceType } from "@/domain/Optimize";
import { translation } from "@/i18n";
import { nsApp } from "@/i18n/locales/app";
import React, {
  useMemo,
  useState,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { useMatch } from "react-router-dom";
import {
  getInvitationsOrganizations,
  getOrganizations,
} from "../../../api/organizations";
import { OrganizationsItemRaw } from "../../../domain/Organization";
import {
  LdsEndpointVariantContext,
  orgKindToLdsEndpointVariant,
} from "../../../sheetRowsIO/ldsEndpoint";
import { TopNavOnlyLayout } from "../../AppTopNav/TopNavOnlyLayout";

const organizationCtx = createContext<{
  organizations: OrganizationsItemRaw[];
  invitedOrganizations: OrganizationsItemRaw[];
  runReloadOrganizations: (options?: {
    isKeepingCurrentData?: boolean;
  }) => Promise<void>;
  runReloadInvitedOrganizations: () => Promise<void>;
  isLoadedOrganizations: boolean;
  isLoadedInvitedOrganizations: boolean;
  orgServiceType: OrganizationServiceType | null;
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
}>({
  organizations: [],
  invitedOrganizations: [],
  runReloadOrganizations: async () => {
    // nop
  },
  runReloadInvitedOrganizations: async () => {
    // nop
  },
  isLoadedOrganizations: false,
  isLoadedInvitedOrganizations: false,
  orgServiceType: null,
  pageTitle: null,
  setPageTitle: () => {
    // nop
  },
});

const useTranslation = translation.use(nsApp);

/**
 * @description サイドメニュー, 組織設定画面などさまざまな場所で使う組織データを管理
 */
export const AppOrgsAndInvitedOrgsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const params = useMatch("/organizations/:organizationId/*")?.params;
  const organizationId = params?.organizationId;
  const [organizations, setOrganizations] = useState<
    OrganizationsItemRaw[] | null
  >(null);
  const [invitedOrganizations, setInvitedOrganizations] = useState<
    OrganizationsItemRaw[] | null
  >(null);
  const isLoadedInvitedOrganizations = invitedOrganizations !== null;
  const isLoadedOrganizations = organizations !== null;
  const [_, setError] = useState<unknown>(null);

  const isInvitedOrganization = useMemo(() => {
    if (!isLoadedInvitedOrganizations || !organizationId) return false;
    return invitedOrganizations.some((org) => org.org_id === organizationId);
  }, [invitedOrganizations, organizationId, isLoadedInvitedOrganizations]);

  const currentOrganization = useMemo(() => {
    return organizations?.find((org) => org.org_id === organizationId) ?? null;
  }, [organizations, organizationId]);

  const orgServiceType = useMemo<OrganizationServiceType | null>(() => {
    if (!currentOrganization) return null;
    return currentOrganization.org_kind.startsWith("planium")
      ? "planium"
      : "optium";
  }, [currentOrganization]);

  // シートの SheetRowsIOWSLDSHost が使う LDS 接続先 variant。context で配ることで
  // コミット済みツリーに紐づけ、レンダー副作用のグローバル書き換えを避ける。
  const ldsEndpointVariant = useMemo(
    () => orgKindToLdsEndpointVariant(currentOrganization?.org_kind ?? null),
    [currentOrganization]
  );

  const runReloadOrganizations = useCallback(
    async ({
      isKeepingCurrentData = false,
    }: { isKeepingCurrentData?: boolean } = {}) => {
      if (!isKeepingCurrentData) setOrganizations(null);
      try {
        const response = await getOrganizations();
        setOrganizations(response);
      } catch (err: unknown) {
        setError(() => {
          if (typeof err == "object") {
            const e = {
              ...err,
              message: getAPIErrorDetailAndMessage(err),
            } as Error;
            throw e;
          } else {
            throw new Error("不明なエラー");
          }
        });
      }
    },
    []
  );

  const runReloadInvitedOrganizations = useCallback(async () => {
    setInvitedOrganizations(null);
    try {
      const response = await getInvitationsOrganizations();
      response && setInvitedOrganizations(response);
    } catch (err: unknown) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    }
  }, []);

  useEffect(() => {
    runReloadOrganizations();
    runReloadInvitedOrganizations();
  }, []);

  const [pageTitle, setPageTitle] = useState<string | null>(null);

  const organizationCtxValue = useMemo(
    () => ({
      organizations: organizations ?? [],
      invitedOrganizations: invitedOrganizations ?? [],
      runReloadInvitedOrganizations,
      runReloadOrganizations,
      isLoadedOrganizations,
      isLoadedInvitedOrganizations,
      orgServiceType,
      pageTitle,
      setPageTitle,
    }),
    [
      organizations,
      invitedOrganizations,
      runReloadOrganizations,
      runReloadInvitedOrganizations,
      isLoadedOrganizations,
      isLoadedInvitedOrganizations,
      orgServiceType,
      pageTitle,
      setPageTitle,
    ]
  );

  if (!isLoadedOrganizations || !isLoadedInvitedOrganizations) {
    return (
      <div style={{ height: "100dvh" }}>
        <LoadingCover loadingText={t.app.loading.organizations()} />
      </div>
    );
  }

  // NOTE: エラー分岐も organizationCtx.Provider の内側で描画する。外側だと useAppOrgsAndInvitedOrgs が
  // 既定値の空配列を返し、ヘッダーのグローバルメニューで組織切替が出なくなる。
  return (
    <organizationCtx.Provider value={organizationCtxValue}>
      <LdsEndpointVariantContext.Provider value={ldsEndpointVariant}>
        {isInvitedOrganization && organizationId ? (
          <TopNavOnlyLayout>
            <ErrorDisplay
              errorDisplayType="invitedOrganization"
              invitedOrgInfo={{
                organizationId,
                organizationName:
                  invitedOrganizations.find(
                    (org) => org.org_id === organizationId
                  )?.name ?? "組織",
              }}
            />
          </TopNavOnlyLayout>
        ) : organizationId && !currentOrganization ? (
          <TopNavOnlyLayout>
            <ErrorDisplay errorDisplayType="notFound" />
          </TopNavOnlyLayout>
        ) : (
          children
        )}
      </LdsEndpointVariantContext.Provider>
    </organizationCtx.Provider>
  );
};

export const useAppOrgsAndInvitedOrgs = () => {
  return useContext(organizationCtx);
};

export const usePageTitleEffect = (title: string | null) => {
  const { setPageTitle } = useAppOrgsAndInvitedOrgs();
  useEffect(() => {
    setPageTitle(title);
    return () => {
      setPageTitle(null);
    };
  }, [title]);
};
